/**
 * A searchable emoji catalogue, and a guess at the right one from a name.
 *
 * The picker used to be twenty-five curated glyphs and nothing else. That is a
 * good *browsing* set — a 3,000-glyph wall is a worse answer to "which one says
 * holiday" than two dozen options — but it is a hopeless *finding* set, because
 * the thing people name a trip album after is where they went, and no amount of
 * scrolling twenty-five pictures produces a Spanish flag.
 *
 * So the curated set stays as what you see first, and search reaches the rest.
 * Every country flag is here, because a trip has a destination; the general
 * glyphs carry keywords rather than a single label, since somebody typing
 * "food" and somebody typing "dinner" want the same square.
 *
 * Flags are built from ISO 3166-1 alpha-2 codes rather than pasted: each is the
 * two letters shifted into the regional-indicator block, which is both shorter
 * than 250 literals and impossible to get subtly wrong.
 */

export interface EmojiEntry {
  emoji: string;
  label: string;
  /** Everything this should be findable by, lower case. */
  keywords: readonly string[];
}

/** "ES" -> Spanish flag. Two regional indicators, which render as one flag. */
function flagFor(code: string): string {
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map(
      (letter) => 0x1f1e6 + letter.charCodeAt(0) - 65,
    ),
  );
}

/*
 * Country name, then anything else somebody might type for it — the adjective,
 * the local name, and for the places people photograph most, the cities.
 * "Barcelona 2026" should find Spain without the person knowing they wanted a
 * country at all.
 */
const COUNTRIES: readonly (readonly string[])[] = [
  ["AD", "Andorra"],
  ["AE", "United Arab Emirates", "uae", "dubai", "abu dhabi", "emirates"],
  ["AF", "Afghanistan"],
  ["AL", "Albania", "albanian", "tirana"],
  ["AM", "Armenia", "yerevan"],
  ["AO", "Angola"],
  ["AR", "Argentina", "argentinian", "buenos aires", "patagonia"],
  ["AT", "Austria", "austrian", "vienna", "salzburg", "innsbruck"],
  ["AU", "Australia", "australian", "sydney", "melbourne", "brisbane", "perth"],
  ["AZ", "Azerbaijan", "baku"],
  ["BA", "Bosnia and Herzegovina", "bosnia", "sarajevo", "mostar"],
  ["BB", "Barbados"],
  ["BD", "Bangladesh", "dhaka"],
  ["BE", "Belgium", "belgian", "brussels", "bruges", "antwerp", "ghent"],
  ["BG", "Bulgaria", "bulgarian", "sofia"],
  ["BH", "Bahrain"],
  ["BO", "Bolivia", "la paz"],
  ["BR", "Brazil", "brazilian", "rio", "rio de janeiro", "sao paulo"],
  ["BS", "Bahamas"],
  ["BW", "Botswana"],
  ["BY", "Belarus", "minsk"],
  ["BZ", "Belize"],
  ["CA", "Canada", "canadian", "toronto", "vancouver", "montreal", "banff"],
  ["CH", "Switzerland", "swiss", "zurich", "geneva", "zermatt", "interlaken"],
  ["CL", "Chile", "santiago", "atacama"],
  ["CM", "Cameroon"],
  ["CN", "China", "chinese", "beijing", "shanghai", "hong kong"],
  ["CO", "Colombia", "bogota", "medellin", "cartagena"],
  ["CR", "Costa Rica"],
  ["CU", "Cuba", "havana"],
  ["CY", "Cyprus"],
  ["CZ", "Czechia", "czech republic", "czech", "prague", "brno"],
  ["DE", "Germany", "german", "berlin", "munich", "hamburg", "cologne"],
  ["DK", "Denmark", "danish", "copenhagen"],
  ["DO", "Dominican Republic", "punta cana"],
  ["DZ", "Algeria"],
  ["EC", "Ecuador", "quito", "galapagos"],
  ["EE", "Estonia", "estonian", "tallinn"],
  ["EG", "Egypt", "egyptian", "cairo", "luxor", "hurghada"],
  [
    "ES",
    "Spain",
    "spanish",
    "espana",
    "barcelona",
    "madrid",
    "seville",
    "valencia",
    "mallorca",
    "ibiza",
    "malaga",
    "tenerife",
    "canary islands",
  ],
  ["ET", "Ethiopia"],
  ["FI", "Finland", "finnish", "helsinki", "lapland"],
  ["FJ", "Fiji"],
  [
    "FR",
    "France",
    "french",
    "paris",
    "nice",
    "lyon",
    "marseille",
    "bordeaux",
    "corsica",
  ],
  [
    "GB",
    "United Kingdom",
    "uk",
    "britain",
    "british",
    "england",
    "english",
    "london",
    "scotland",
    "scottish",
    "edinburgh",
    "wales",
    "welsh",
    "manchester",
  ],
  ["GE", "Georgia", "tbilisi"],
  ["GH", "Ghana"],
  [
    "GR",
    "Greece",
    "greek",
    "athens",
    "santorini",
    "crete",
    "rhodes",
    "mykonos",
    "corfu",
  ],
  ["GT", "Guatemala"],
  ["HR", "Croatia", "croatian", "zagreb", "dubrovnik", "split", "hvar"],
  ["HU", "Hungary", "hungarian", "budapest"],
  ["ID", "Indonesia", "bali", "jakarta", "lombok"],
  ["IE", "Ireland", "irish", "dublin", "galway"],
  ["IL", "Israel", "tel aviv", "jerusalem"],
  ["IN", "India", "indian", "delhi", "mumbai", "goa", "kerala", "jaipur"],
  ["IQ", "Iraq"],
  ["IR", "Iran", "tehran"],
  ["IS", "Iceland", "icelandic", "reykjavik"],
  [
    "IT",
    "Italy",
    "italian",
    "rome",
    "milan",
    "venice",
    "florence",
    "naples",
    "sicily",
    "tuscany",
    "amalfi",
  ],
  ["JM", "Jamaica"],
  ["JO", "Jordan", "petra", "amman"],
  ["JP", "Japan", "japanese", "tokyo", "kyoto", "osaka", "hokkaido"],
  ["KE", "Kenya", "nairobi"],
  ["KH", "Cambodia", "angkor", "phnom penh"],
  ["KR", "South Korea", "korea", "korean", "seoul", "busan"],
  ["KZ", "Kazakhstan"],
  ["LB", "Lebanon", "beirut"],
  ["LK", "Sri Lanka", "colombo"],
  ["LT", "Lithuania", "lithuanian", "vilnius"],
  ["LU", "Luxembourg"],
  ["LV", "Latvia", "latvian", "riga"],
  ["MA", "Morocco", "moroccan", "marrakech", "fez", "casablanca"],
  ["MC", "Monaco"],
  ["MD", "Moldova"],
  ["ME", "Montenegro", "kotor", "budva"],
  ["MK", "North Macedonia", "macedonia", "skopje", "ohrid"],
  ["MT", "Malta", "valletta", "gozo"],
  ["MU", "Mauritius"],
  ["MV", "Maldives"],
  ["MX", "Mexico", "mexican", "cancun", "tulum", "mexico city", "oaxaca"],
  ["MY", "Malaysia", "kuala lumpur", "borneo"],
  ["NA", "Namibia"],
  ["NG", "Nigeria", "lagos"],
  [
    "NL",
    "Netherlands",
    "dutch",
    "holland",
    "amsterdam",
    "rotterdam",
    "utrecht",
  ],
  [
    "NO",
    "Norway",
    "norwegian",
    "oslo",
    "bergen",
    "lofoten",
    "tromso",
    "fjords",
  ],
  ["NP", "Nepal", "kathmandu", "everest", "himalaya"],
  ["NZ", "New Zealand", "auckland", "queenstown", "wellington"],
  ["PA", "Panama"],
  ["PE", "Peru", "lima", "cusco", "machu picchu"],
  ["PH", "Philippines", "manila", "palawan", "cebu"],
  ["PK", "Pakistan"],
  [
    "PL",
    "Poland",
    "polish",
    "polska",
    "warsaw",
    "warszawa",
    "krakow",
    "cracow",
    "gdansk",
    "wroclaw",
    "poznan",
    "zakopane",
    "tatry",
    "tatra",
  ],
  [
    "PT",
    "Portugal",
    "portuguese",
    "lisbon",
    "porto",
    "algarve",
    "madeira",
    "azores",
  ],
  ["QA", "Qatar", "doha"],
  ["RO", "Romania", "romanian", "bucharest", "transylvania"],
  ["RS", "Serbia", "belgrade"],
  ["RU", "Russia", "russian", "moscow"],
  ["SA", "Saudi Arabia", "riyadh"],
  ["SE", "Sweden", "swedish", "stockholm", "gothenburg"],
  ["SG", "Singapore"],
  ["SI", "Slovenia", "slovenian", "ljubljana", "bled"],
  ["SK", "Slovakia", "slovakian", "bratislava"],
  ["SN", "Senegal"],
  ["TH", "Thailand", "thai", "bangkok", "phuket", "chiang mai", "krabi"],
  ["TN", "Tunisia"],
  ["TR", "Turkey", "turkish", "istanbul", "cappadocia", "antalya"],
  ["TW", "Taiwan", "taipei"],
  ["TZ", "Tanzania", "zanzibar", "kilimanjaro"],
  ["UA", "Ukraine", "ukrainian", "kyiv", "kiev", "lviv"],
  ["UG", "Uganda"],
  [
    "US",
    "United States",
    "usa",
    "america",
    "american",
    "new york",
    "california",
    "florida",
    "las vegas",
    "hawaii",
    "chicago",
    "boston",
  ],
  ["UY", "Uruguay"],
  ["UZ", "Uzbekistan", "samarkand"],
  ["VE", "Venezuela"],
  ["VN", "Vietnam", "hanoi", "ho chi minh", "halong"],
  ["ZA", "South Africa", "cape town", "johannesburg", "kruger"],
  ["ZW", "Zimbabwe", "victoria falls"],
];

/**
 * Not a country, but people look for them.
 *
 * Deliberately no Scotland, Wales or England flags. They exist as emoji, but
 * they are tag sequences fourteen UTF-16 units long, and the server accepts at
 * most eight — so offering them would mean a glyph somebody can pick and then
 * cannot save. `USABLE_EMOJI` below enforces that rather than trusting this
 * paragraph.
 */
const OTHER_FLAGS: readonly EmojiEntry[] = [
  {
    emoji: "\u{1F3F3}\u{FE0F}\u{200D}\u{1F308}",
    label: "Pride flag",
    keywords: ["pride", "rainbow", "flag", "lgbt"],
  },
  {
    emoji: "\u{1F3C1}",
    label: "Chequered flag",
    keywords: ["race", "racing", "finish", "flag"],
  },
];

const FLAG_EMOJI: readonly EmojiEntry[] = [
  ...COUNTRIES.map((row) => {
    const [code, name, ...aliases] = row as [string, string, ...string[]];
    return {
      emoji: flagFor(code),
      label: name,
      keywords: [
        name.toLowerCase(),
        code.toLowerCase(),
        "flag",
        "country",
        ...aliases,
      ],
    };
  }),
  ...OTHER_FLAGS,
];

/**
 * Everything that is not a flag. Keyworded rather than labelled, because
 * "food", "dinner" and "restaurant" all want the same square and only one of
 * them can be the name.
 */
const GENERAL_EMOJI: readonly EmojiEntry[] = [
  {
    emoji: "✈️",
    label: "Flight",
    keywords: [
      "flight",
      "plane",
      "travel",
      "airport",
      "trip",
      "holiday",
      "vacation",
    ],
  },
  {
    emoji: "🏖",
    label: "Beach",
    keywords: ["beach", "sand", "holiday", "summer", "coast", "seaside"],
  },
  {
    emoji: "🏔",
    label: "Mountains",
    keywords: ["mountain", "mountains", "alps", "hiking", "peak", "summit"],
  },
  {
    emoji: "🎉",
    label: "Party",
    keywords: ["party", "celebration", "fun", "night out"],
  },
  {
    emoji: "🎂",
    label: "Birthday",
    keywords: ["birthday", "cake", "party", "anniversary"],
  },
  {
    emoji: "🍕",
    label: "Food",
    keywords: ["food", "pizza", "dinner", "restaurant", "eating", "lunch"],
  },
  { emoji: "🍜", label: "Noodles", keywords: ["noodles", "ramen", "asian"] },
  {
    emoji: "🌊",
    label: "Sea",
    keywords: ["sea", "ocean", "waves", "swimming", "surf"],
  },
  {
    emoji: "🏕",
    label: "Camping",
    keywords: ["camping", "camp", "tent", "outdoors", "wild"],
  },
  {
    emoji: "🎿",
    label: "Skiing",
    keywords: ["ski", "skiing", "snow", "winter", "slopes"],
  },
  {
    emoji: "🏂",
    label: "Snowboarding",
    keywords: ["snowboard", "snowboarding"],
  },
  {
    emoji: "🚗",
    label: "Road trip",
    keywords: ["road trip", "car", "drive", "driving"],
  },
  { emoji: "🚐", label: "Van life", keywords: ["van", "camper", "motorhome"] },
  {
    emoji: "🚲",
    label: "Cycling",
    keywords: ["cycling", "bike", "bicycle", "ride"],
  },
  { emoji: "🏍", label: "Motorbike", keywords: ["motorbike", "motorcycle"] },
  {
    emoji: "🌅",
    label: "Sunrise",
    keywords: ["sunrise", "sunset", "morning", "golden hour"],
  },
  { emoji: "🌴", label: "Tropical", keywords: ["tropical", "palm", "island"] },
  {
    emoji: "🌸",
    label: "Spring",
    keywords: ["spring", "blossom", "flowers", "cherry"],
  },
  { emoji: "🍂", label: "Autumn", keywords: ["autumn", "fall", "leaves"] },
  { emoji: "❄️", label: "Winter", keywords: ["winter", "snow", "cold"] },
  { emoji: "☀️", label: "Summer", keywords: ["summer", "sun", "sunny", "hot"] },
  {
    emoji: "🎄",
    label: "Christmas",
    keywords: ["christmas", "xmas", "december", "holidays"],
  },
  {
    emoji: "🎃",
    label: "Halloween",
    keywords: ["halloween", "october", "pumpkin"],
  },
  {
    emoji: "🎆",
    label: "New year",
    keywords: ["new year", "fireworks", "sylwester"],
  },
  {
    emoji: "💍",
    label: "Wedding",
    keywords: ["wedding", "marriage", "engagement", "ring", "slub", "wesele"],
  },
  {
    emoji: "👶",
    label: "New baby",
    keywords: ["baby", "newborn", "christening"],
  },
  { emoji: "🐶", label: "Dog", keywords: ["dog", "puppy", "pet", "pets"] },
  { emoji: "🐱", label: "Cat", keywords: ["cat", "kitten"] },
  { emoji: "🐴", label: "Horse", keywords: ["horse", "riding", "equestrian"] },
  {
    emoji: "🦁",
    label: "Safari",
    keywords: ["safari", "lion", "wildlife", "animals", "zoo"],
  },
  {
    emoji: "🐠",
    label: "Diving",
    keywords: ["diving", "snorkelling", "fish", "reef", "scuba"],
  },
  {
    emoji: "🎸",
    label: "Music",
    keywords: ["music", "gig", "concert", "band", "guitar"],
  },
  { emoji: "🎤", label: "Singing", keywords: ["singing", "karaoke"] },
  { emoji: "⛺", label: "Festival", keywords: ["festival"] },
  {
    emoji: "🗺",
    label: "Exploring",
    keywords: ["exploring", "map", "adventure", "travel"],
  },
  {
    emoji: "🏛",
    label: "Sightseeing",
    keywords: ["sightseeing", "museum", "history", "culture", "monument"],
  },
  {
    emoji: "⛪",
    label: "Church",
    keywords: ["church", "cathedral", "religion"],
  },
  {
    emoji: "🏰",
    label: "Castle",
    keywords: ["castle", "palace", "fairytale", "zamek"],
  },
  {
    emoji: "🚂",
    label: "Train journey",
    keywords: ["train", "rail", "railway", "journey"],
  },
  { emoji: "🛳", label: "Cruise", keywords: ["cruise", "ship", "boat"] },
  { emoji: "⛵", label: "Sailing", keywords: ["sailing", "yacht", "sail"] },
  {
    emoji: "🍷",
    label: "Drinks",
    keywords: ["drinks", "wine", "bar", "vineyard"],
  },
  { emoji: "🍺", label: "Beer", keywords: ["beer", "pub", "brewery", "piwo"] },
  {
    emoji: "☕",
    label: "Coffee",
    keywords: ["coffee", "cafe", "breakfast", "brunch"],
  },
  {
    emoji: "📸",
    label: "Photos",
    keywords: ["photos", "camera", "photography", "shoot"],
  },
  {
    emoji: "🎓",
    label: "Graduation",
    keywords: ["graduation", "university", "school", "degree"],
  },
  { emoji: "🏠", label: "Home", keywords: ["home", "house", "moving"] },
  {
    emoji: "👨‍👩‍👧",
    label: "Family",
    keywords: ["family", "kids", "children", "parents", "rodzina"],
  },
  {
    emoji: "👯",
    label: "Friends",
    keywords: ["friends", "mates", "crew", "group", "gang", "znajomi"],
  },
  { emoji: "⚽", label: "Football", keywords: ["football", "soccer", "match"] },
  { emoji: "🏀", label: "Basketball", keywords: ["basketball"] },
  { emoji: "🏃", label: "Running", keywords: ["running", "marathon", "race"] },
  { emoji: "🏊", label: "Swimming", keywords: ["swimming", "pool", "swim"] },
  {
    emoji: "🧗",
    label: "Climbing",
    keywords: ["climbing", "bouldering", "rock"],
  },
  { emoji: "🎣", label: "Fishing", keywords: ["fishing", "fish", "lake"] },
  {
    emoji: "🔥",
    label: "Campfire",
    keywords: ["fire", "campfire", "bonfire", "barbecue"],
  },
  {
    emoji: "🌍",
    label: "World",
    keywords: ["world", "earth", "global", "everywhere"],
  },
  {
    emoji: "❤️",
    label: "Love",
    keywords: ["love", "heart", "couple", "valentine", "anniversary"],
  },
  {
    emoji: "⭐",
    label: "Favourites",
    keywords: ["star", "favourite", "best", "highlights"],
  },
  { emoji: "🎁", label: "Gifts", keywords: ["gift", "present"] },
  {
    emoji: "🥾",
    label: "Hiking",
    keywords: ["hiking", "trek", "walk", "trail", "boots"],
  },
  {
    emoji: "🛶",
    label: "Kayaking",
    keywords: ["kayak", "canoe", "paddle", "river"],
  },
  {
    emoji: "🎡",
    label: "Theme park",
    keywords: ["theme park", "funfair", "rides"],
  },
  { emoji: "🏝", label: "Island", keywords: ["island", "desert island"] },
  { emoji: "🌋", label: "Volcano", keywords: ["volcano", "crater"] },
  { emoji: "🏜", label: "Desert", keywords: ["desert", "dunes", "sahara"] },
  {
    emoji: "🌉",
    label: "City",
    keywords: ["city", "bridge", "skyline", "urban"],
  },
  { emoji: "🌃", label: "Night", keywords: ["night", "nightlife", "evening"] },
];

/**
 * The same rule the server applies, applied here.
 *
 * `emojiSchema` accepts at most eight UTF-16 units and exactly one grapheme.
 * A glyph that fails it can be picked and then not saved, which is a worse
 * experience than never being offered — so the catalogue is filtered by the
 * real constraint rather than by a comment asking future editors to remember
 * it. `Intl.Segmenter` is what the server uses too.
 */
function isSavable(entry: EmojiEntry): boolean {
  if (entry.emoji.length > 8) return false;
  try {
    return [...new Intl.Segmenter().segment(entry.emoji)].length === 1;
  } catch {
    // No Segmenter: fall back to the length rule alone rather than emptying
    // the picker on an old browser.
    return true;
  }
}

export const ALL_EMOJI: readonly EmojiEntry[] = [
  ...GENERAL_EMOJI,
  ...FLAG_EMOJI,
].filter(isSavable);

/** Fold accents so "krakow" finds "Kraków" and "espana" finds "España". */
function normalise(value: string): string {
  return value.toLocaleLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

/**
 * Search, ranked so that what somebody typed in full comes before what merely
 * contains it: typing "pol" offers Poland before Napoli.
 */
export function searchEmoji(query: string, limit = 60): EmojiEntry[] {
  const needle = normalise(query);
  if (!needle) return [];

  const scored: { entry: EmojiEntry; score: number }[] = [];

  for (const entry of ALL_EMOJI) {
    let best = 0;
    for (const keyword of entry.keywords) {
      const word = normalise(keyword);
      if (word === needle) best = Math.max(best, 3);
      else if (word.startsWith(needle)) best = Math.max(best, 2);
      else if (word.includes(needle)) best = Math.max(best, 1);
    }
    if (best > 0) scored.push({ entry, score: best });
  }

  scored.sort(
    (a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label),
  );
  return scored.slice(0, limit).map((row) => row.entry);
}

/**
 * Guess an emoji from an album or group name.
 *
 * Only ever a *suggestion*, offered beside the name and never applied on its
 * own. Silently choosing a glyph would be worse than offering none: "Chris's
 * leaving do" is not a Christmas album, and a wrong icon that appeared by
 * itself is both puzzling and annoying to undo.
 *
 * Whole words only, and nothing shorter than four letters, which is what stops
 * "Ana in Poland" matching on "an". The longest match wins, so "new zealand"
 * beats "new year".
 */
export function suggestEmoji(name: string): EmojiEntry | null {
  const haystack = normalise(name);
  if (haystack.length < 4) return null;

  let winner: EmojiEntry | null = null;
  let winningLength = 0;

  for (const entry of ALL_EMOJI) {
    for (const keyword of entry.keywords) {
      const word = normalise(keyword);
      if (word.length < 4 || word.length <= winningLength) continue;
      // Word boundaries, so "poland" matches and "polar" does not.
      const pattern = new RegExp(
        `(^|[^\\p{L}\\p{N}])${escapeForRegExp(word)}($|[^\\p{L}\\p{N}])`,
        "u",
      );
      if (pattern.test(haystack)) {
        winner = entry;
        winningLength = word.length;
      }
    }
  }

  return winner;
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Whether this browser can actually draw a flag.
 *
 * Windows ships no flag glyphs at all. The characters are perfectly valid and
 * save correctly — the same album shows a Spanish flag on a phone — but Segoe
 * UI Emoji has no picture for them, so the two regional indicators fall back to
 * their letters and 🇪🇸 renders as "ES". Nothing is broken; there is simply no
 * artwork on the machine.
 *
 * Detected by drawing one and looking at it. A real flag is coloured; the
 * letter fallback is drawn in the fill colour and nothing else, so a single
 * non-grey pixel is the whole test. Guessing from the user agent would be
 * wrong the moment somebody installs an emoji font.
 */
let flagSupport: boolean | null = null;

export function supportsFlagEmoji(): boolean {
  if (flagSupport !== null) return flagSupport;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 24;
    canvas.height = 24;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return (flagSupport = true);

    context.fillStyle = "#000";
    context.textBaseline = "top";
    context.font = "20px sans-serif";
    context.fillText("\u{1F1EA}\u{1F1F8}", 0, 0);

    const { data } = context.getImageData(0, 0, 24, 24);
    for (let i = 0; i < data.length; i += 4) {
      const [r, g, b, a] = [data[i]!, data[i + 1]!, data[i + 2]!, data[i + 3]!];
      // Any pixel where the channels disagree is paint, not text.
      if (a > 0 && (Math.abs(r - g) > 12 || Math.abs(g - b) > 12)) {
        return (flagSupport = true);
      }
    }
    return (flagSupport = false);
  } catch {
    // No canvas to ask. Assume the flags are fine rather than warning wrongly.
    return (flagSupport = true);
  }
}
